import { reactive } from 'vue'
import { type DeviceInfo, type E2Info, type ErrorData, ErrorSeverity, type ErrorState, type FlattenedErrorData } from '@/utils/errors.ts'
import { type E2, UUID } from '@ated/e2-core'
import packageJson from '../../package.json'

export const errorState = reactive<ErrorState>({
  hasError: false,
  currentError: null,
  errorStack: [],
  showDialog: false
})

const getDeviceInfo = (): DeviceInfo => {
  const userAgentData = navigator.userAgent
  // Expect deprecation warning
  const platform = navigator.platform

  // OS detection
  let os = 'Unknown OS'
  if (/Windows/.test(userAgentData)) os = 'Windows'
  else if (/Mac/.test(userAgentData)) os = 'macOS'
  else if (/Linux/.test(userAgentData)) os = 'Linux'
  else if (/Android/.test(userAgentData)) os = 'Android'
  else if (/iPhone|iPad|iPod/.test(userAgentData)) os = 'iOS'

  // Browser detection
  let browser = 'Unknown Browser'
  let browserVersion = ''

  if (/Firefox\/\d+/.test(userAgentData)) {
    browser = 'Firefox'
    browserVersion = userAgentData.match(/Firefox\/(\d+\.\d+)/)?.[1] || ''
  } else if (/Edge\/\d+|Edg\/\d+/.test(userAgentData)) {
    browser = 'Edge'
    browserVersion = userAgentData.match(/Edge\/(\d+\.\d+)|Edg\/(\d+\.\d+)/)?.[1] || ''
  } else if (/Chrome\/\d+/.test(userAgentData)) {
    browser = 'Chrome'
    browserVersion = userAgentData.match(/Chrome\/(\d+\.\d+)/)?.[1] || ''
  } else if (/Safari\/\d+/.test(userAgentData) && !/Chrome/.test(userAgentData)) {
    browser = 'Safari'
    browserVersion = userAgentData.match(/Version\/(\d+\.\d+)/)?.[1] || ''
  } else if (/MSIE|Trident/.test(userAgentData)) {
    browser = 'Internet Explorer'
    browserVersion = userAgentData.match(/MSIE (\d+\.\d+)|rv:(\d+\.\d+)/)?.[1] || ''
  } else if (/Opera|OPR\//.test(userAgentData)) {
    browser = 'Opera'
    browserVersion = userAgentData.match(/Opera\/(\d+\.\d+)|OPR\/(\d+\.\d+)/)?.[1] || ''
  }

  const isMobile = /Mobi|Android|iPhone|iPad|iPod/.test(userAgentData)

  return {
    os,
    platform,
    browser,
    browserVersion,
    userAgent: userAgentData,
    isMobile,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    devicePixelRatio: window.devicePixelRatio || 1
  }
}

const getPerformanceInfo = () => {
  const performanceInfo: ErrorData['performance'] = {}

  // Memory info - only available in Chrome
  const memory = (performance as any).memory
  if (memory) {
    performanceInfo.memory = {
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
      totalJSHeapSize: memory.totalJSHeapSize,
      usedJSHeapSize: memory.usedJSHeapSize
    }
  }

  // TODO ask FB if there is more performance data to gather

  // Prepare return for multiple performance keys return
  return Object.keys(performanceInfo).length > 0 ? performanceInfo : undefined
}

const getE2Info = (e2: E2): E2Info => {
  const themeId = e2.themeService.currentTheme.id
  const produktId = e2.productService.product?.id || undefined
  // @ts-expect-error
  const clientVersion = GITVERSION
  const coreVersion = packageJson.dependencies['@ated/e2-core'].replace('^', '')
  // TODO SessionId currently is not implemented so we generate new UUID -> '00000000-0000-0000-0000-000000000000'
  const sessionId = e2.sessionService.sessionId.value || '00000000-0000-0000-0000-000000000000'

  return { themeId, produktId, coreVersion, clientVersion, sessionId }
}

export async function captureError(
  error: Error | unknown,
  componentInfo: Record<string, any> | null = null,
  additionalContext: Record<string, any> = {},
  severity: ErrorSeverity = ErrorSeverity.ERROR,
  e2?: E2,
  manualTrigger?: boolean | false
): Promise<boolean> {
  console.error('Error captured:', error)

  // Early return if error reporting is disabled AND not manually triggered
  const reportSetting = e2?.themeService.currentTheme.features?.['report.error.dialog']
  if (reportSetting === 'off' && !manualTrigger) {
    return false
  }

  const deviceInfo = getDeviceInfo()

  const errorData: ErrorData = {
    message: error instanceof Error ? error.message : 'Unknown error',
    stack: error instanceof Error ? error.stack : undefined,
    timestamp: new Date().toISOString(),
    severity,
    componentInfo,
    deviceInfo,
    browserInfo: {
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      url: window.location.href,
      referrer: document.referrer,
      language: navigator.language
    },
    additionalContext
  }

  errorData.performance = getPerformanceInfo()

  if (e2) {
    errorData.e2Info = getE2Info(e2)
  }

  // Update error state
  errorState.currentError = errorData
  errorState.errorStack.push(errorData)
  errorState.hasError = true

  // See if feature about showing dialog is on user or silent
  // If "user" show dialog, if "silent" sent error without opening, last option is "off" in which case do nothing
  if (reportSetting === 'user' || manualTrigger) {
    errorState.showDialog = true
  } else if (reportSetting === 'silent' && e2) {
    try {
      await reportErrorToServer(errorState.currentError, e2)
    } catch (reportError) {
      console.error('Failed to report error to server:', reportError)
    }
  }

  return false
}

export function closeErrorDialog(): void {
  errorState.showDialog = false
}

function flattenErrorDataJSON(errData: ErrorData, prefix = '') {
  return Object.keys(errData).reduce((acc: FlattenedErrorData, key) => {
    const prefixedKey = prefix ? `${prefix}.${key}` : key

    // Check if the value is an object and not null
    if (typeof errData[key] === 'object' && errData[key] !== null && !Array.isArray(errData[key])) {
      // Recursively flatten nested objects
      Object.assign(acc, flattenErrorDataJSON(errData[key], prefixedKey))
    } else {
      // Add the key-value pair to the result
      acc[prefixedKey] = errData[key]
    }
    return acc
  }, {})
}

export async function reportErrorToServer(errorData: ErrorData, e2: E2, customMessage?: string): Promise<void> {
  // Inject custom message if it is given
  if (customMessage) {
    errorData.customMessage = customMessage
  }

  return e2.sessionService.reportSessionError(
    errorData.e2Info?.sessionId || 'undefined',
    new UUID().toString(),
    errorData.severity.toString(),
    flattenErrorDataJSON(errorData),
    errorData.e2Info?.themeId
  )
}

export function registerWindowErrorHandlers(): void {
  // Handle uncaught promise rejections
  window.addEventListener('unhandledrejection', async (event: PromiseRejectionEvent) => {
    await captureError(event.reason, {
      source: 'unhandledrejection'
    })
  })

  // Handle global errors
  window.addEventListener('error', async (event: ErrorEvent) => {
    await captureError(event.error || new Error(event.message), {
      source: 'window.onerror',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    })
  })
}
